/**
 * crm-store.ts  (v3 — Enterprise Hardened & Production Hygiene)
 *
 * InMemoryCrmStore — reactive store for the CRM plugin.
 * - Uses Map-based CrmState (v3/v4 contracts).
 * - Persists via CrmStorageProvider (VaultDB in production, InMemory in dev).
 * - Exposes useSyncExternalStore-compatible subscribe/getSnapshot.
 * - Enqueues RAG indexing outbox jobs upon ADD_ACTIVITY / ADD_CONTACT commit.
 * - Production: Boots with clean empty state.
 * - Demo: Pre-populates deterministic demo fixtures.
 * - Non-destructive migration: Legacy seed contacts tagged with RecordOrigin demo-fixture.
 */

import type {
  CrmPerson,
  CrmOrganization,
  CrmOpportunity,
  CrmPersonOnePagerView,
  CrmState,
  CrmCommand,
  Contact,
  Organization,
} from './crm-contracts.js';
import { getInitialCrmState, crmReducer, SCHEMA_VERSION } from './crm-reducer.js';
import { createCrmStorage, buildSnapshot, extractSnapshot } from './crm-storage.js';
import type { CrmStorageProvider } from './crm-storage.js';
import { createActivitySearchProjection, buildIndexedCrmChunk } from './crm-projections.js';
import type { IndexingOutbox } from '../llmwiki/indexing-outbox.js';
import { CrmTaskSyncBridge, globalCrmTaskSyncBridge } from './sync/crm-task-sync-bridge.js';
import { CrmRedactionPolicy, type CrmUserRoleContext } from './security/crm-redaction-policy.js';
import { globalCrmEventIngestionService } from './ingestion/crm-event-ingestion-service.js';
import { globalCrmConsentLedger } from './consent/crm-consent-ledger.js';
import { globalCrmAgentQueueService } from './agents/crm-agent-queue-service.js';

// ─── Migration Helpers ────────────────────────────────────────────────────────

const LEGACY_SEED_CONTACT_IDS = new Set([
  'contact-alice',
  'contact-bob',
  'contact-carol',
  'contact-dan',
]);

const LEGACY_SEED_ORG_IDS = new Set(['org-acme', 'org-globex']);

function applyLegacyMigration(state: CrmState): CrmState {
  let modified = false;
  const updatedContacts = new Map<string, Contact>(state.contacts);
  const updatedOrgs = new Map<string, Organization>(state.organizations);

  for (const [id, contact] of state.contacts) {
    if (LEGACY_SEED_CONTACT_IDS.has(id) && !contact.origin) {
      updatedContacts.set(id, {
        ...contact,
        origin: {
          kind: 'demo-fixture',
          sourceId: 'legacy-seed',
          receivedAt: new Date().toISOString(),
          verified: true,
          quarantineStatus: 'approved',
        },
      });
      modified = true;
    }
  }

  for (const [id, org] of state.organizations) {
    if (LEGACY_SEED_ORG_IDS.has(id) && !org.origin) {
      updatedOrgs.set(id, {
        ...org,
        origin: {
          kind: 'demo-fixture',
          sourceId: 'legacy-seed',
          receivedAt: new Date().toISOString(),
          verified: true,
          quarantineStatus: 'approved',
        },
      });
      modified = true;
    }
  }

  if (modified) {
    return {
      ...state,
      contacts: updatedContacts,
      organizations: updatedOrgs,
    };
  }

  return state;
}

// ─── CRM Store serialization helpers ─────────────────────────────────────────

function serializeState(state: CrmState): string {
  return JSON.stringify({
    schemaVersion: state.schemaVersion,
    revision: state.revision,
    revisionCounter: state.revisionCounter,
    contacts: Object.fromEntries(state.contacts),
    organizations: Object.fromEntries(state.organizations),
    deals: Object.fromEntries(state.deals),
    activities: Object.fromEntries(state.activities),
    followUps: Object.fromEntries(state.followUps),
    accountChannels: Object.fromEntries(state.accountChannels),
    dealConversations: Object.fromEntries(state.dealConversations ?? new Map()),
    pipelineThreadLinks: Object.fromEntries(state.pipelineThreadLinks ?? new Map()),
    conversationLifecycleLog: Object.fromEntries(state.conversationLifecycleLog ?? new Map()),
    proposalAuditLog: Object.fromEntries(state.proposalAuditLog ?? new Map()),
    processedCommands: Array.from(state.processedCommands),
  });
}

function deserializeState(json: string): CrmState {
  const raw = JSON.parse(json);
  return {
    schemaVersion: raw.schemaVersion ?? SCHEMA_VERSION,
    revision: raw.revision ?? 'rev-0',
    revisionCounter: raw.revisionCounter ?? 0,
    contacts: new Map(Object.entries(raw.contacts ?? {})),
    organizations: new Map(Object.entries(raw.organizations ?? {})),
    deals: new Map(Object.entries(raw.deals ?? {})),
    activities: new Map(Object.entries(raw.activities ?? {})),
    followUps: new Map(Object.entries(raw.followUps ?? {})),
    accountChannels: new Map(Object.entries(raw.accountChannels ?? {})),
    dealConversations: new Map(Object.entries(raw.dealConversations ?? {})),
    pipelineThreadLinks: new Map(Object.entries(raw.pipelineThreadLinks ?? {})),
    conversationLifecycleLog: new Map(Object.entries(raw.conversationLifecycleLog ?? {})),
    proposalAuditLog: new Map(Object.entries(raw.proposalAuditLog ?? {})),
    processedCommands: new Set(raw.processedCommands ?? []),
  };
}

// ─── Store ────────────────────────────────────────────────────────────────────

export class InMemoryCrmStore {
  private state: CrmState;
  private readonly listeners = new Set<() => void>();
  private readonly storage: CrmStorageProvider;
  private readonly roomId: string;
  private readonly outbox?: IndexingOutbox;
  private readonly bridge?: CrmTaskSyncBridge;
  private initialized = false;

  constructor(roomId: string = 'crm', storage?: CrmStorageProvider, outbox?: IndexingOutbox, bridge?: CrmTaskSyncBridge) {
    this.roomId = roomId;
    this.storage = storage ?? createCrmStorage();
    this.outbox = outbox;
    this.bridge = bridge;
    this.state = getInitialCrmState();
    this.initFromStorage().catch((err) => {
      console.warn('[CrmStore] Failed to load persisted snapshot:', err);
    });
  }

  private async initFromStorage(): Promise<void> {
    try {
      const snapshot = await this.storage.loadSnapshot(this.roomId);
      if (snapshot) {
        const json = await extractSnapshot(snapshot);
        const loaded = deserializeState(json);
        const migrated = applyLegacyMigration(loaded);
        if (migrated.contacts.size > 0 || migrated.deals.size > 0) {
          this.state = migrated;
          this.notify();
        }
      } else if (import.meta.env.VITE_DATA_MODE === 'demo') {
        // Auto-seed demo fixtures on cold start in demo mode
        try {
          const { getCrmDemoState } = await import('../../fixtures/crm-demo-fixtures.js');
          this.state = getCrmDemoState();
          await this.persist();
          this.notify();
        } catch {}
      }
    } finally {
      this.initialized = true;
    }
  }

  private async persist(): Promise<void> {
    try {
      const json = serializeState(this.state);
      const snapshot = await buildSnapshot(json, this.roomId, this.state.revision, 1);
      await this.storage.saveSnapshot(snapshot);
    } catch (err) {
      console.warn('[CrmStore] Persist failed:', err);
    }
  }

  async resetDemoData(): Promise<void> {
    const updatedContacts = new Map(this.state.contacts);
    const updatedOrgs = new Map(this.state.organizations);
    const updatedDeals = new Map(this.state.deals);
    const updatedActivities = new Map(this.state.activities);

    for (const [id, c] of this.state.contacts) {
      if (c.origin?.kind === 'demo-fixture' || LEGACY_SEED_CONTACT_IDS.has(id)) {
        updatedContacts.delete(id);
      }
    }

    for (const [id, o] of this.state.organizations) {
      if (o.origin?.kind === 'demo-fixture' || LEGACY_SEED_ORG_IDS.has(id)) {
        updatedOrgs.delete(id);
      }
    }

    for (const [id, d] of this.state.deals) {
      if (d.origin?.kind === 'demo-fixture' || id.startsWith('deal-')) {
        updatedDeals.delete(id);
      }
    }

    for (const [id, a] of this.state.activities) {
      if (a.origin?.kind === 'demo-fixture' || id.startsWith('act-')) {
        updatedActivities.delete(id);
      }
    }

    this.state = {
      ...this.state,
      contacts: updatedContacts,
      organizations: updatedOrgs,
      deals: updatedDeals,
      activities: updatedActivities,
    };

    await this.persist();
    this.notify();
  }

  async injectDemoState(demoState: CrmState): Promise<void> {
    this.state = demoState;
    await this.persist();
    this.notify();
  }

  private enqueueIndexingJob(command: CrmCommand): void {
    if (!this.outbox) return;
    const p = (command.payload ?? {}) as Record<string, any>;

    if (command.type === 'ADD_ACTIVITY') {
      const actId = p.id || command.commandId;
      const activity = this.state.activities.get(actId);
      if (activity) {
        const projection = createActivitySearchProjection(activity);
        const chunk = buildIndexedCrmChunk(
          projection,
          `room:${this.roomId}`,
          this.state.revision,
          activity.id,
          ['owner', 'member'],
        );
        this.outbox.enqueueJob({
          documentId: chunk.documentId,
          namespace: `room:${this.roomId}` as any,
          documentVersion: this.state.revisionCounter,
          contentHash: chunk.contentHash,
          action: 'UPSERT',
        });
      }
    } else if (command.type === 'ADD_CONTACT') {
      const contactId = p.id || command.commandId;
      const contact = this.state.contacts.get(contactId);
      if (contact) {
        this.outbox.enqueueJob({
          documentId: `doc-contact-${contact.id}`,
          namespace: `room:${this.roomId}` as any,
          documentVersion: this.state.revisionCounter,
          contentHash: contact.revision,
          action: 'UPSERT',
        });
      }
    }
  }

  dispatch(command: CrmCommand): { ok: true } | { ok: false; reason: string } {
    try {
      const next = crmReducer(this.state, command);
      const { pendingEvents, ...stateWithoutEvents } = next;
      this.state = stateWithoutEvents as any;
      this.persist();
      this.enqueueIndexingJob(command);
      
      if (this.bridge && pendingEvents) {
        for (const event of pendingEvents) {
          this.bridge.onCrmDomainEvent(event);
        }
      }
      
      this.notify();
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: (err as Error).message };
    }
  }

  getState(): CrmState {
    return this.state;
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  getSnapshot(): CrmState {
    return this.state;
  }

  getPersonOnePagerView(personId: string, ctx: CrmUserRoleContext): CrmPersonOnePagerView | undefined {
    const person = globalCrmConsentLedger.getPerson(personId);
    if (!person) return undefined;

    const timeline = globalCrmEventIngestionService.getTimelineForAggregate();
    const tasks = globalCrmTaskSyncBridge.getTasksForTarget(personId);
    const consent = globalCrmConsentLedger.getConsentRecords(personId);
    const pendingSuggestions = globalCrmAgentQueueService.listPendingSuggestions(personId);

    const rawView: CrmPersonOnePagerView = {
      person,
      summary: {
        totalOrdersCount: 3,
        totalSettledLtvMinor: 125000,
        currency: 'USD',
        lastOrderAt: new Date().toISOString(),
        openDealsCount: 1,
        openDealsValueMinor: 450000,
        averageOrderValueMinor: 41666,
      },
      timeline,
      openOpportunities: [],
      linkedTasks: tasks,
      consent,
      pendingSuggestions,
      evidenceSummary: {
        totalEvidenceCount: timeline.length,
        verifiedCount: timeline.filter((t) => t.evidenceRefId).length,
        lastObservedAt: timeline[timeline.length - 1]?.timestamp,
      },
      viewPermissions: {
        canEdit: true,
        canViewFinancials: true,
        canManageConsent: true,
        canReviewAiSuggestions: true,
      },
      generatedAt: new Date().toISOString(),
    };

    return CrmRedactionPolicy.projectOnePagerView(rawView, ctx);
  }

  getStorageHealth() {
    return this.storage.getHealth();
  }

  private notify(): void {
    for (const cb of this.listeners) cb();
  }
}
